import { useCallback, useMemo } from "react";
import useAuthentication from "@/Hooks/useAuthentication";
import useRoutesHandler from "@/Hooks/useRouteHandler";
import { stringUtility } from "@repo/utilities/string.utility.ts";

export default function useCheckPermission() {
  type PermissionName = "view" | "update" | "delete" | "create";

  const { subRoute, activeRoutes } = useRoutesHandler();
  const {
    services: { authService },
  } = useAuthentication();

  const permissionsMap = authService?.data?.permissions;
  const subTab = ["edit", "add", "view"].includes(subRoute as string)
    ? activeRoutes?.[activeRoutes.length - 2] || subRoute
    : subRoute;
  const tabDataCode = stringUtility.replaceCharAll(subTab as string, "-", "_");
  const currentModulePermissions = permissionsMap?.[tabDataCode] || [];

  const permissionsNameArray = useMemo(() => {
    return currentModulePermissions.map(
      (permissionUnit: { name: string }) => permissionUnit?.name?.split("_")[0]
    );
  }, [currentModulePermissions]);

  function checkPermission(permissionName: PermissionName): boolean {
    if (Array.isArray(permissionsMap) && permissionsMap?.includes("*")) {
      return true;
    }
    return permissionsNameArray?.includes(permissionName);
  }

  const hasPermission = useMemo(() => {
    return {
      view: checkPermission("view"),
      create: checkPermission("create"),
      update: checkPermission("update"),
      delete: checkPermission("delete"),
    };
  }, [permissionsNameArray, permissionsMap]);

  const hasFeaturePermission = useCallback(
    (featureName: string) => {
      if (Array.isArray(permissionsMap) && permissionsMap?.includes("*"))
        return true;
      for (const item of currentModulePermissions) {
        if (item?.name?.toLowerCase() == featureName?.toLowerCase())
          return true;
      }
      return false;
    },
    [currentModulePermissions]
  );

  const filterColumnPermissions = useCallback(
    (columns: any[]) =>
      columns.filter((column) => hasFeaturePermission(column?.column_name)),
    [currentModulePermissions]
  );

  return {
    hasPermission,
    permissionsMap,
    authService,
    hasFeaturePermission,
    filterColumnPermissions,
  };
}
